import {DateLimits, ValidDateType, ValidDate} from './components/DesignSystem/Fields/types';

export const isValidEmail = (email: string): boolean => {
  const pattern =
    /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
  if (email.length > 256) {
    return false;
  }

  if (!pattern.test(email)) {
    return false;
  }

  // Further checking of some things regex can't handle
  const [account, address] = email.split('@');
  if (account.length > 64) {
    return false;
  }

  const domainParts = address.split('.');
  if (domainParts.some((part) => part.length > 63)) {
    return false;
  }

  return true;
};

export const convertDateTo = {
  obj: (date: string): Date => new Date(date),
  iso: (dateObj: Date): string => dateObj.toISOString().slice(0, 10),
  epoch: (dateObj: Date): number => dateObj.valueOf(),
};

export const isValidDate = (date: string, dateLimits?: DateLimits): ValidDate => {
  const {obj: dateObj, iso: dateISO, epoch: dateEpoch} = convertDateTo;

  // make sure there is a format of xxxx-xx-xx, where x is a digit
  const dateObject = dateObj(date);

  const isValid = {isValid: true};
  const isNotValid = {isValid: false};

  if (Number.isNaN(dateObject.getTime())) {
    return isNotValid;
  }

  if (dateObject.getUTCFullYear() < 1000) {
    return isNotValid;
  }

  if (!dateLimits && dateISO(dateObject) === date) {
    return isValid;
  }

  if (dateLimits) {
    const {min, max} = dateLimits;

    // make sure date matches isostring format prior to comparing
    if (dateISO(dateObject) === date) {
      const epochDate = dateEpoch(dateObject);
      const epochMin = min ? dateEpoch(min) : undefined;
      const epochMax = max ? dateEpoch(max) : undefined;

      if (epochMin && epochMax) {
        return {isValid: epochMin <= epochDate && epochDate <= epochMax, type: ValidDateType.RANGE};
      }

      if (epochMin) {
        return {isValid: epochMin <= epochDate, type: ValidDateType.MIN};
      }

      if (epochMax) {
        return {isValid: epochDate <= epochMax, type: ValidDateType.MAX};
      }

      return isValid;
    }
  }

  return isNotValid;
};

export const isValidCompanyNumber = (companyNumber: string): boolean => {
  if (companyNumber.length > 10) {
    return false;
  }

  // Most English companies have numbers of length 8 (covered by the empty character in the first space of the capturing group)
  // Other companies may have a two letter prefix to their number, namely NI and SC (Northern Irish and Scottish, respectively).
  // The two letter prefix can be followed by 6-8 characters, so can be a total of 10 characters long.
  // However, prefixed numbers can sometimes still be presented without their prefixes, so make sure we can still capture those (can be 6 characters)
  const ukPattern = /^(([A-Za-z]{2})?\d{6,8})$/;
  if (companyNumber.match(ukPattern)) {
    return true;
  }

  return false;
};

export const isValidPhone = (value: string, isMobilePhone = false): boolean => {
  // First 0 in these is optional. If it's added it is removed in `getPayloadFromData` before sending to API
  // This is so that brokers can call the phone number more easily in Salesforce
  let isMatching: boolean;
  if (isMobilePhone) {
    // Must start with 7
    const pattern = /^0?7\d{9}$/;
    isMatching = Boolean(value.match(pattern));
  } else {
    const pattern = /^0?[1-9]\d{9}$/;
    isMatching = Boolean(value.match(pattern));
  }
  return isMatching;
};

export const numberPattern = /^\d+$/g;
